/**
 * Display copy for the server-derived `review_status` (epic 2026-09-04
 * sub-task 11) — ONE module, so the contacts-table cells and the export panel
 * cannot word the same state two ways.
 *
 * NOTHING HERE DERIVES A STATUS. `review_status` arrives on the wire from
 * `nu_assist_shared/domain/review_status.py` and is rendered verbatim; the
 * three client derivations this epic deleted (`skipReason`, `exportStateBadge`,
 * the bare-truthy `contact_id_thrive` reads) are not coming back through a copy
 * file. What this module adds is the QUALIFIER — which compare finding a row
 * carries — read off the orthogonal flags the same payload already ships
 * (`is_w2` / `is_w3` / `thrive_match_type`). A qualifier names a fact ALONGSIDE
 * the status, never instead of it.
 *
 * TWO COLUMNS, TWO QUESTIONS (operator, 2026-09-08, hand-testing the running
 * app). One `review_status` feeds both, but each column answers only its own
 * header:
 *
 *   - **Thrive ID** — "do we know this person in Thrive?" The id, `Not found`,
 *     or `Not compared yet`. See `THRIVE_*` below.
 *   - **Added to search** — "what happened with THIS search?" Added / Failed /
 *     Skipped / Needs review / `Not added yet`. See `SEARCH_STATUS_LABEL`.
 *
 * "Skipped", not "Ignored" (fu5, 2026-09-10). The vocabulary line was the last
 * place in this module still listing the old word: `SEARCH_STATUS_LABEL.ignored`
 * itself was renamed by sub-task 01, and 02 finished the sweep across the modal
 * and the export panel. The WIRE VALUE is still `"ignored"` — the rename is
 * display-only, and deliberately so.
 *
 * The old single vocabulary put `already_in_thrive` / `not_in_thrive` /
 * `not_compared` under "Added to search", where none of them answers the
 * column's own question: a row reading "Already in Thrive" left "was it added?"
 * unanswered. Those three now read `Not added yet` there — true in every
 * pre-add state — and the Thrive-side fact moves to the column that asks for it.
 *
 * All copy in English (app-content language rule).
 */

import type { ImportContactRow } from "@nu-assist/contracts";

/**
 * The word the "Added to search" column shows per server state — what happened
 * with THIS search, and nothing else.
 *
 *  - `exported` reads "Added", never "Exported" (#743 retired the verb; the
 *    column header already says where it was added).
 *  - `already_in_thrive` / `not_in_thrive` / `not_compared` all read
 *    "Not added yet". They are three different Thrive-side facts and ONE
 *    search-side fact, and this column reports the search-side one. The
 *    Thrive-side difference between them is what the Thrive ID column renders.
 *  - "Not added yet", not a dash: a dash is ambiguous between "no" and
 *    "unknown", and the phrase is explicitly true in every pre-add state.
 *  - `ignored` reads "Skipped" as of fu5 (2026-09-10). The persisted value
 *    keeps its name — renaming a column is a migration, and this is a copy
 *    change — but every word the recruiter sees is now "Skip"/"Skipped",
 *    because the ACTION was renamed to `Skip` and a state whose button no
 *    longer exists sends people looking for it. Jared, 2026-09-08: *"I'm not
 *    even sure ignore needs to be a thing."*
 */
export const SEARCH_STATUS_LABEL: Record<NonNullable<ImportContactRow["review_status"]>, string> = {
  not_compared: "Not added yet",
  ignored: "Skipped",
  to_review: "To review",
  failed: "Failed",
  exported: "Added",
  already_in_thrive: "Not added yet",
  not_in_thrive: "Not added yet",
};

/** The Thrive ID column when a compare looked and Thrive has no such contact.
 *
 *  It is DATED at the call site, and the asymmetry with the id is deliberate: a
 *  Thrive id is durable and does not age, so it carries no date; a negative
 *  result does — "not in Thrive" from three weeks ago is worth re-checking. */
export const THRIVE_NOT_FOUND = "Not found";

/** The Thrive ID column when NO compare has ever classified this row. Carries
 *  no date, and needs none: it means never. */
export const THRIVE_NOT_COMPARED = "Not compared yet";

// STATUS_DATE_SEPARATOR is GONE (2026-09-08). It joined a status word to its
// date on ONE line ("Added · Sep 7"); the date now has its own line in both
// columns, and a line of its own needs no separator. The operator asked for
// the split because the status word can run long — "To review — identity
// mismatch" — and the date behind it wrapped, reading as a continuation of the
// sentence rather than as a record of when.

/** What stands in for a date the row should carry and does not.
 *
 *  Found by the operator hand-testing import
 *  `3d1943b9-fc68-4935-adef-eab9f68d3910` (1,777 contacts, every one of them
 *  classified before migration 0040 introduced `compared_at`): the rows read a
 *  bare "Not found" and nothing else. Rendering nothing was the DESIGNED
 *  behaviour — inventing a date would be a false claim — but silence is
 *  indistinguishable, to the person reading it, from the blank-status bug this
 *  epic exists to remove.
 *
 *  ONE string, no longer one per verb: the status word beside it already names
 *  the event ("Added", "Skipped", "Failed", "Not found"), so the line no longer
 *  has to name it a second time. */
export const DATE_NOT_RECORDED = "date not recorded";

/**
 * The orthogonal duplicate-LinkedIn marker on a contacts-table row — CAUSE
 * first, then consequence.
 *
 * It used to say only `will duplicate on export`, which is the consequence, and
 * the operator hit the gap that leaves: a FRESHLY uploaded file already showed
 * the marker on some rows and read as a bug. It was not one.
 * `different_person_registry` is firm-wide and permanent by design and is keyed
 * on the canonical LinkedIn URL, so a decision recorded in an earlier run — by
 * anyone, on any import — applies the moment a later file carries that URL. The
 * row stated the consequence and never the cause, so there was nothing on
 * screen from which the behaviour could be recognised as intended.
 *
 * The quoted fragment is the ACTION'S OWN LABEL (`CONFLICT_ACTION_LABELS
 * .notSamePerson`), quoted so it reads as a citation rather than as a
 * description. Someone who has clicked that button recognises what happened
 * without a hover; someone who has not is told there was a decision, which is
 * the part that stops it reading as a malfunction.
 *
 * It deliberately does NOT say WHO or WHEN. The decision is often not this
 * row's and often not this recruiter's — claiming otherwise would replace one
 * wrong impression with another — so the ⓘ carries that, and the Different
 * people section in the compare modal carries the attribution in full.
 *
 * Still worded as a FUTURE action in its second half. Its opposite — the W1
 * qualifier below, where Thrive ALREADY holds several contacts on one URL —
 * used to read "duplicate LinkedIn in Thrive" against this marker's "dup.
 * LinkedIn", and the operator misread one as the other. Two facts that are
 * opposites must not be near-homographs.
 */
export const WILL_DUPLICATE_MARKER = 'Tagged "not the same person". Will duplicate on export.';

/** The same fact as `WILL_DUPLICATE_MARKER` worn as a filter chip. Short
 *  because it sits in a chip row; same verb so the chip and the rows it
 *  selects say the same thing. */
export const WILL_DUPLICATE_CHIP_LABEL = "Will duplicate";

/** Behind the ⓘ on the `will duplicate on export` marker.
 *
 *  The two duplicate states in this cell are near-opposites and the operator
 *  read one as the other while hand-testing (2026-09-08). Rewording them apart
 *  helped; it could not carry WHY they differ in the width of a table cell,
 *  which is what these two notes are for. */
export const WILL_DUPLICATE_HINT = {
  what:
    "Someone marked this LinkedIn URL as belonging to a different person than " +
    "the Thrive contact it matched, so this row will be added as a SECOND " +
    "contact on that URL. Nothing is wrong yet. This is what the export is " +
    "about to do.",
  // THE part that answers the operator's actual question. The decision is
  // firm-wide and keyed on the URL, so it can predate this import entirely —
  // which is exactly why a freshly uploaded file can show the marker on its
  // very first render and look broken.
  //
  // A SECOND field rather than a longer first one: two paragraphs are two
  // questions ("what is this" and "why is it here already"), and the second is
  // the one nobody knew to ask. Newline characters would not have done it —
  // they collapse to a space in HTML, which is how this note lost its break the
  // first time it was written.
  whyAlready:
    "The decision is firm-wide and permanent, and it may have been made on an " +
    "earlier import by anyone in the firm. A brand-new file carrying the same " +
    'URL inherits it immediately. Compare to Thrive lists these under "Same ' +
    'LinkedIn URL on two people", where it can be undone.',
} as const;

/** Behind the ⓘ on the `already duplicated in Thrive` qualifier — the other
 *  half of the pair above, and the one that is already true. */
export const ALREADY_DUPLICATED_HINT =
  "Thrive already holds more than one contact on this LinkedIn URL, so we " +
  "cannot tell which one this row is. Nothing here can fix it: merge them in " +
  "Thrive (or correct the wrong URL) and compare again.";

type QualifiedRow = Pick<ImportContactRow, "is_w2" | "is_w3" | "thrive_match_type">;

/**
 * WHICH compare finding this row carries, or null when it carries none.
 *
 * The three arms are the three findings Compare records, in the order the
 * conflict sections rank them: a W2 identity mismatch, a W3 unverified name
 * match, a W1 ambiguous LinkedIn match. A row can only be flagged for one of
 * them in practice; the order settles the theoretical overlap rather than
 * describing a real one.
 *
 * The W1 arm reads "already duplicated in Thrive" — the state of Thrive right
 * now, several contacts on one URL — against `WILL_DUPLICATE_MARKER`'s "will
 * duplicate on export", which is a write we have not made yet. Telling those
 * two apart in words is the only thing stopping a recruiter reading one as the
 * other, and before this wording they read nearly alike.
 */
export function conflictQualifier(row: QualifiedRow): string | null {
  if (row.is_w2) return "identity mismatch";
  if (row.is_w3) return "unverified name match";
  if (row.thrive_match_type === "duplicate") return "already duplicated in Thrive";
  return null;
}

/**
 * The "Added to search" cell's full label: the search-side word, plus the
 * qualifier when the row is waiting on a decision.
 *
 * ONLY `to_review` is qualified. `not_compared` has no finding yet; `ignored` /
 * `exported` / `failed` each record a decision or an outcome that came AFTER
 * the finding; and the two Thrive-presence states read "Not added yet" here, so
 * appending a compare finding to them would answer the OTHER column's question
 * in this one — the split this vocabulary exists to make.
 *
 * Example from the approved preview: `To review — identity mismatch`.
 */
export function searchStatusLabel(
  row: QualifiedRow & Pick<ImportContactRow, "review_status">
): string {
  const base = SEARCH_STATUS_LABEL[row.review_status];
  if (row.review_status !== "to_review") return base;
  const qualifier = conflictQualifier(row);
  return qualifier ? `${base} (${qualifier})` : base;
}
